<template>
  <span>
    <AppSelectCheckboxes
      v-if="showCheckboxesView()"
      v-model="inputValue"
      v-bind="$attrs"
      :multiple="multiple"
      :return-object="returnObject"
      :items="regionSelections"
      :label="label"
      item-text="name"
      item-value="id"
    />
    <v-select
      v-else
      v-bind="[$attrs]"
      v-model="inputValue"
      :items="regionSelections"
      :multiple="multiple"
      :return-object="returnObject"
      :label="label"
      :menu-props="menuProperties"
      :search-input.sync="search"
      item-text="name"
      item-value="id"
      hide-no-data
      outlined
      :clearable="clearable"
      :data-test="dataTest"
      :class="[selectMixinClass, '']"
      :hide-details="hideDetails"
      @change="
        evt => {
          inputVisibility(evt, 'change');
          $emit('change', evt);
        }
      "
      @focus="focused"
      @blur="
        evt => {
          inputVisibility(evt);
          $emit('blur');
        }
      "
    >
      <template #item="{item, attrs}">
        <v-list-item
          v-bind="attrs"
          :key="item.id"
          @click="
            item.children && item.children.length > 0
              ? toggleRegionGroupValues(item)
              : toggleRegionValue(item)
          "
        >
          <v-list-item-action
            v-if="multiple"
            :class="{
              'ml-2': multiple && !!item.parentRegionId
            }"
          >
            <v-checkbox
              color="accent"
              :input-value="inputValue"
              :value="item.id"
            />
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>

      <template #append-outer><slot name="append-outer"/></template>
    </v-select>
  </span>
</template>
<script>
import { defineComponent } from "vue";
import AppSelectCheckboxes from "./AppSelectCheckboxes.vue";
import {
  useRegionHelper,
  MasterRegionFragment
} from "@composables/masterdata/regions";
import selectsMixin from "./selects_mixin";
import { uniq, uniqBy } from "lodash-es";

import groupSelectOptions from "@/utils/inputs/group_select_items";

export default defineComponent({
  name: "AppSelectRegions",
  components: { AppSelectCheckboxes },
  mixins: [selectsMixin],
  props: {
    menuProps: {
      type: Object,
      default: () => {}
    },
    possibleValues: {
      type: Array,
      default: () => []
    },
    prepend: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: "Regions"
    },
    hideDetails: {
      type: Boolean,
      default: true
    },
    setDefault: {
      type: Boolean,
      default: false
    },
    includeDisabled: {
      type: Boolean,
      default: false
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    dataTest: {
      type: String,
      default: "regionSelect"
    }
  },
  setup() {
    const {
      masterRegions,
      masterRegionsActive,
      withMissingRegionSelect
    } = useRegionHelper();
    return {
      masterRegions,
      masterRegionsActive,
      withMissingRegionSelect
    };
  },
  data() {
    return {
      updater: 0,
      search: "",
      menuProperties: {}
    };
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.updater++;
      }
    },
    regionSelections() {
      if (this.possibleValues.length > 0) {
        return this.masterRegions.filter(x =>
          this.possibleValues.includes(x.id)
        );
      }
      let regions = this.masterRegionsActive;

      if (this.includeDisabled) {
        regions = this.masterRegions.filter(x =>
          ["active", "disabled"].includes(x.status)
        );
      }

      regions = this.withMissingRegionSelect(this.inputValue, {
        customItems: regions,
        multiple: this.multiple,
        returnObject: this.returnObject
      });

      if (regions.some(x => !!x.parentRegionId)) {
        regions = groupSelectOptions(regions, {
          keyParentValue: "parentRegionId",
          multiple: this.multiple,
          keyValue: "id",
          keyText: "name"
        });
      }

      return regions;
    }
  },
  created() {
    this.menuProperties.nudgeBottom = this.nudgeBottom;
    this.menuProperties.closeOnContentClick = !this.multiple;
    this.menuProperties.contentProps = { "data-test": "regionSelect" };
  },
  methods: {
    // TODO these 3 methods should be isolated and be composable :I
    toggleRegionGroupValues(parentRegion) {
      const regionsToToggle = parentRegion.children.map(x =>
        this.returnObject ? x : x.id
      );
      regionsToToggle.push(this.returnObject ? parentRegion : parentRegion.id);

      if (this.hasAnyChildRegionSelected(parentRegion)) {
        this.inputValue = (this.inputValue ?? []).filter(v => {
          if (this.returnObject) {
            return regionsToToggle.includes(v.id) ? false : true;
          }
          return regionsToToggle.includes(v) ? false : true;
        });
        return;
      }

      this.inputValue = uniq((this.inputValue ?? []).concat(regionsToToggle));
    },
    toggleRegionValue(region) {
      if (this.multiple) {
        const inputValue = this.inputValue ?? [];
        if (this.returnObject) {
          if (inputValue.map(x => x.id).includes(region.id)) {
            this.inputValue = inputValue.filter(v => {
              return v.id != region.id;
            });
            return;
          }

          this.inputValue = uniqBy(inputValue.concat([region]), "id");
          return;
        }

        if (inputValue.includes(region.id)) {
          this.inputValue = inputValue.filter(vId => {
            return vId != region.id;
          });
          return;
        }

        this.inputValue = uniq(inputValue.concat([region.id]));
        return;
      }

      if (this.returnObject) {
        if (!this.inputValue) {
          this.inputValue = region;
          return;
        }
        this.inputValue = region.id == this.inputValue.id ? null : region;
        return;
      }
      this.inputValue = region.id == this.inputValue ? null : region.id;
    },
    hasAnyChildRegionSelected(parentRegion) {
      const regionIds = parentRegion.children.map(x => x.id);
      regionIds.push(parentRegion.id);

      if (this.multiple) {
        return (this.inputValue ?? []).some(v => {
          if (this.returnObject) {
            return regionIds.includes(v.id);
          }
          return regionIds.includes(v);
        });
      }

      return regionIds.includes(this.inputValue);
    }
  },
  fragments: {
    regionFields: MasterRegionFragment
  }
});
</script>
